// ─── Filter framework: the bar layout (f-02062) ───────────────────
// One BarLayout per user per listId, stored server-side (one layout for every
// device). `items` is the left-to-right order of the bar; saved / standard
// filters are placed through `pins` (pills inside the pinnedFilters group, max
// 5) or `tabs` (the tab row, max 8) — ONE placement per ref, and a ref in
// neither is "menu only" (the default). Over a cap is refused with a message,
// never evicted.
//
// Rules (reconcileBarLayout runs on every read and write, server and client):
//   - retired / unknown item ids are DROPPED (the only thing that deletes);
//   - conditionally unavailable items (quick:reviewed while auto-review is on)
//     are kept in storage and only HIDDEN at render time (planBarRender);
//   - filtersMenu, search and pinnedFilters are movable but never removable,
//     so they are always present (search stays in `items` even when it renders
//     on its own row, so switching back to inline restores its position);
//   - single-instance items appear once; spacers / dividers repeat as
//     'space#n' with stable unique suffixes, never sit at an edge and never
//     repeat side by side.
import { isFilterAvailable } from './registry.js';
export const BAR_LAYOUT_SCHEMA = 1;
export const BAR_PIN_CAP = 5;
export const BAR_TAB_CAP = 8;
export const BAR_ITEMS_CAP = 30;
export const BAR_PAGE_SIZES = [25, 50, 100];
export const BAR_FIXED_ITEM_IDS = [
    'filtersMenu', 'pinnedFilters', 'search', 'addFilter', 'advanced', 'saveState', 'count', 'sort', 'clear', 'customize',
];
export const BAR_REPEATABLE_KINDS = ['space', 'flexibleSpace', 'divider'];
export const FILTER_DISPLAY_MODES = ['iconAndText', 'textOnly', 'iconOnly'];
export const BAR_DISPLAY_MODES = ['auto', 'iconAndText', 'iconOnly', 'textOnly'];
export const BAR_SEARCH_PLACEMENTS = ['inline', 'above', 'below'];
export const BAR_TABS_PLACEMENTS = ['above', 'below'];
// ─── Ids ──────────────────────────────────────────────────────────
const FIXED = new Set(BAR_FIXED_ITEM_IDS);
const REPEATABLE = new Set(BAR_REPEATABLE_KINDS);
export function barItemKind(id) {
    if (FIXED.has(id))
        return id;
    if (id.startsWith('quick:') && id.length > 6)
        return 'quick';
    const hash = id.indexOf('#');
    const kind = hash > 0 ? id.slice(0, hash) : id;
    if (REPEATABLE.has(kind))
        return kind;
    return null;
}
export function isRepeatableBarItem(id) {
    const k = barItemKind(id);
    return k === 'space' || k === 'flexibleSpace' || k === 'divider';
}
/** A fresh 'kind#n' id not used in `existing`. */
export function newRepeatableBarItemId(kind, existing) {
    let n = 1;
    const used = new Set(existing);
    while (used.has(`${kind}#${n}`))
        n++;
    return `${kind}#${n}`;
}
/** The palette entry an id belongs to ('divider#3' → 'divider'). */
export function barPaletteId(id) {
    return isRepeatableBarItem(id) ? barItemKind(id) : id;
}
function quickFieldOf(id) {
    return id.startsWith('quick:') ? id.slice(6) : null;
}
/** Is the id part of this list's bar at all (known + in its palette)? */
export function isKnownBarItem(id, descriptor) {
    const kind = barItemKind(id);
    if (!kind)
        return false;
    if (kind === 'quick') {
        const key = quickFieldOf(id);
        return descriptor.bar.palette.includes(id) && descriptor.fields.some((f) => f.key === key && !!f.quick);
    }
    return descriptor.bar.palette.includes(barPaletteId(id));
}
export function isBarItemRemovable(id, descriptor) {
    return !descriptor.bar.movableButNotRemovable.includes(id);
}
/** Conditionally available for this viewer (render-time only). */
export function isBarItemAvailable(id, descriptor, features) {
    const key = quickFieldOf(id);
    if (!key)
        return true;
    const f = descriptor.fields.find((x) => x.key === key);
    return !!f && isFilterAvailable(f.availability, features);
}
// ─── Refs ─────────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A ref this list understands: a saved-filter uuid or 'std:<key>' of one of
 *  ITS standard filters (two lists may both have std:all; each layout is per
 *  list, so they never collide). */
export function isValidBarRef(ref, descriptor) {
    if (typeof ref !== 'string')
        return false;
    if (ref.startsWith('std:'))
        return descriptor.standardFilters.some((s) => `std:${s.key}` === ref);
    return UUID_RE.test(ref);
}
function isStdRefAvailable(ref, descriptor, features) {
    if (!ref.startsWith('std:'))
        return true;
    const s = descriptor.standardFilters.find((x) => `std:${x.key}` === ref);
    return !!s && isFilterAvailable(s.availability, features);
}
/** The composite key per-user filter state is stored under — (listId, ref),
 *  so std:all on Tickets and std:all on another list never collide. */
export function filterUserStateKey(listId, ref) {
    return `${listId}\u001f${ref}`;
}
// ─── Defaults + reconcile ─────────────────────────────────────────
export function defaultBarLayout(descriptor) {
    const d = descriptor.bar.defaultLayout;
    return {
        schema: BAR_LAYOUT_SCHEMA,
        items: [...d.items],
        pins: d.pins.map((p) => ({ ref: p.ref })),
        tabs: d.tabs.map((p) => ({ ref: p.ref })),
        searchPlacement: d.searchPlacement,
        tabsPlacement: d.tabsPlacement,
        displayMode: d.displayMode,
        pageSize: d.pageSize,
    };
}
function pick(v, allowed, fallback) {
    return typeof v === 'string' && allowed.includes(v) ? v : fallback;
}
/** Remove repeatables at either edge and collapse same-kind neighbours. */
export function tidyBarItems(items) {
    const out = [];
    for (const id of items) {
        const prev = out[out.length - 1];
        if (isRepeatableBarItem(id)) {
            if (out.length === 0)
                continue;
            if (prev && isRepeatableBarItem(prev) && barItemKind(prev) === barItemKind(id))
                continue;
            if (prev && barItemKind(prev) === 'divider' && barItemKind(id) === 'divider')
                continue;
        }
        out.push(id);
    }
    while (out.length && isRepeatableBarItem(out[out.length - 1]))
        out.pop();
    return out;
}
/** Bring a stored (possibly old / hostile) layout into shape. Never throws. */
export function reconcileBarLayout(raw, ctx) {
    const { descriptor } = ctx;
    const def = defaultBarLayout(descriptor);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return def;
    const o = raw;
    if (o.schema !== undefined && o.schema !== BAR_LAYOUT_SCHEMA)
        return def; // unknown future shape
    // Items: known ids only, single-instance once, repeatables with unique ids.
    const items = [];
    const seen = new Set();
    for (const id of Array.isArray(o.items) ? o.items : def.items) {
        if (typeof id !== 'string' || !isKnownBarItem(id, descriptor))
            continue;
        if (isRepeatableBarItem(id)) {
            const kind = barItemKind(id);
            const fresh = id.includes('#') && !seen.has(id) ? id : newRepeatableBarItemId(kind, [...seen]);
            seen.add(fresh);
            items.push(fresh);
            continue;
        }
        if (seen.has(id))
            continue;
        seen.add(id);
        items.push(id);
    }
    // The movable-but-not-removable items are always present.
    for (const id of descriptor.bar.movableButNotRemovable) {
        if (items.includes(id))
            continue;
        const at = Math.min(Math.max(0, def.items.indexOf(id)), items.length);
        items.splice(at, 0, id);
    }
    let tidy = tidyBarItems(items);
    if (tidy.length > BAR_ITEMS_CAP) {
        const keep = new Set(descriptor.bar.movableButNotRemovable);
        const removable = tidy.filter((id) => !keep.has(id));
        const excess = new Set(removable.slice(removable.length - (tidy.length - BAR_ITEMS_CAP)));
        tidy = tidyBarItems(tidy.filter((id) => !excess.has(id)));
    }
    // Pins / tabs: valid refs, one placement per ref (pins win), caps.
    const placed = new Set();
    const readRefs = (v, cap) => {
        const out = [];
        for (const e of Array.isArray(v) ? v : []) {
            const ref = typeof e === 'string' ? e : e && typeof e === 'object' ? e.ref : undefined;
            if (!isValidBarRef(ref, descriptor) || placed.has(ref))
                continue;
            if (ctx.dropUnavailableRefs && ctx.isRefAvailable && !ref.startsWith('std:') && !ctx.isRefAvailable(ref))
                continue;
            if (out.length >= cap)
                continue;
            placed.add(ref);
            out.push({ ref });
        }
        return out;
    };
    const pins = readRefs(o.pins, BAR_PIN_CAP);
    const tabs = readRefs(o.tabs, BAR_TAB_CAP);
    const pageSize = BAR_PAGE_SIZES.includes(o.pageSize) ? o.pageSize : def.pageSize;
    return {
        schema: BAR_LAYOUT_SCHEMA,
        items: tidy,
        pins,
        tabs,
        searchPlacement: pick(o.searchPlacement, BAR_SEARCH_PLACEMENTS, def.searchPlacement),
        tabsPlacement: pick(o.tabsPlacement, BAR_TABS_PLACEMENTS, def.tabsPlacement),
        displayMode: pick(o.displayMode, BAR_DISPLAY_MODES, def.displayMode),
        pageSize,
    };
}
export function planBarRender(layout, ctx) {
    const searchRow = ctx.compact && layout.searchPlacement === 'inline' ? 'above' : layout.searchPlacement;
    const items = tidyBarItems(layout.items.filter((id) => {
        if (id === 'search' && searchRow !== 'inline')
            return false;
        return isBarItemAvailable(id, ctx.descriptor, ctx.features);
    }));
    const refOk = (ref) => isStdRefAvailable(ref, ctx.descriptor, ctx.features) && (ref.startsWith('std:') || !ctx.isRefAvailable || ctx.isRefAvailable(ref));
    const pins = layout.pins.map((p) => p.ref).filter(refOk);
    const tabs = layout.tabs.map((p) => p.ref).filter(refOk);
    return { items, searchRow, pins, tabs, showTabs: tabs.length > 0, tabsPlacement: layout.tabsPlacement, displayMode: layout.displayMode };
}
/** The mode a pill / tab renders in: the bar-wide Show wins unless 'auto'. */
export function effectiveFilterDisplayMode(bar, own) {
    if (bar !== 'auto')
        return bar;
    return own ?? 'iconAndText';
}
const refuse = (reason, message) => ({ ok: false, reason, message });
/** Can `id` (a fixed id, 'quick:x', a repeatable kind or a minted
 *  'kind#n') be inserted at `index` of the CURRENT items? */
export function canInsertBarItem(layout, id, index, descriptor) {
    if (!isKnownBarItem(id, descriptor))
        return { ok: false, reason: 'unknown_item', message: 'That item is not available in this bar.' };
    const repeatable = isRepeatableBarItem(id);
    if (!repeatable && layout.items.includes(id))
        return { ok: false, reason: 'single_instance', message: 'That item is already in the bar.' };
    if (layout.items.length >= BAR_ITEMS_CAP)
        return { ok: false, reason: 'items_cap', message: `The bar holds at most ${BAR_ITEMS_CAP} items.` };
    const at = Math.max(0, Math.min(index, layout.items.length));
    if (repeatable) {
        if (at === 0 || at === layout.items.length)
            return { ok: false, reason: 'edge', message: "Spaces and dividers can't sit at either end of the bar." };
        if (barItemKind(id) === 'divider') {
            const before = layout.items[at - 1];
            const after = layout.items[at];
            if ((before && barItemKind(before) === 'divider') || (after && barItemKind(after) === 'divider')) {
                return { ok: false, reason: 'adjacent_divider', message: "Two dividers can't sit side by side." };
            }
        }
    }
    return { ok: true };
}
export function insertBarItem(layout, id, index, descriptor) {
    const check = canInsertBarItem(layout, id, index, descriptor);
    if (!check.ok)
        return check;
    const minted = isRepeatableBarItem(id) && !id.includes('#')
        ? newRepeatableBarItemId(barItemKind(id), layout.items)
        : id;
    const items = [...layout.items];
    items.splice(Math.max(0, Math.min(index, items.length)), 0, minted);
    return { ok: true, layout: { ...layout, items }, id: minted };
}
/** Move an item to `toIndex` (the index in the resulting order). Every item
 *  can move, including filtersMenu. */
export function moveBarItem(layout, id, toIndex) {
    const from = layout.items.indexOf(id);
    if (from < 0)
        return refuse('unknown_item', 'That item is not in the bar.');
    const items = [...layout.items];
    items.splice(from, 1);
    items.splice(Math.max(0, Math.min(toIndex, items.length)), 0, id);
    return { ok: true, layout: { ...layout, items }, id };
}
export function removeBarItem(layout, id, descriptor) {
    if (!layout.items.includes(id))
        return refuse('unknown_item', 'That item is not in the bar.');
    if (!isBarItemRemovable(id, descriptor)) {
        return refuse('not_removable', id === 'filtersMenu'
            ? 'The Filters menu holds every filter command, so it always stays. You can move it.'
            : id === 'search' ? 'Search always stays. You can move it or put it on its own row.'
                : 'Pinned filters always stay. Unpin filters to empty the group.');
    }
    return { ok: true, layout: { ...layout, items: layout.items.filter((x) => x !== id) } };
}
/** "Reset to default": default items, search placement and Show — but KEEPS
 *  pins, tabs, tab placement and page size (resetting never silently unpins). */
export function resetBarLayout(layout, descriptor) {
    const d = defaultBarLayout(descriptor);
    return { ...d, pins: layout.pins.map((p) => ({ ...p })), tabs: layout.tabs.map((p) => ({ ...p })), tabsPlacement: layout.tabsPlacement, pageSize: layout.pageSize };
}
export function filterRefPlacement(layout, ref) {
    if (layout.pins.some((p) => p.ref === ref))
        return 'pill';
    if (layout.tabs.some((p) => p.ref === ref))
        return 'tab';
    return 'menu';
}
/**
 * Give a ref its ONE placement ("Move to tabs", "Move to bar", "Show in menu
 * only", or a reorder when the placement is unchanged). Over a cap the move is
 * refused with a message — nothing is ever evicted.
 */
export function placeFilterRef(layout, ref, placement, descriptor, index) {
    if (!isValidBarRef(ref, descriptor))
        return refuse('unknown_ref', 'That filter is not available on this list.');
    const pins = layout.pins.filter((p) => p.ref !== ref);
    const tabs = layout.tabs.filter((p) => p.ref !== ref);
    if (placement === 'pill' && pins.length >= BAR_PIN_CAP) {
        return refuse('pill_cap', `You can pin up to ${BAR_PIN_CAP} filters to the bar. Unpin one first.`);
    }
    if (placement === 'tab' && tabs.length >= BAR_TAB_CAP) {
        return refuse('tab_cap', `You can have up to ${BAR_TAB_CAP} filter tabs. Remove one first.`);
    }
    const put = (list) => {
        const at = index === undefined ? list.length : Math.max(0, Math.min(index, list.length));
        list.splice(at, 0, { ref });
    };
    if (placement === 'pill')
        put(pins);
    if (placement === 'tab')
        put(tabs);
    return { ok: true, layout: { ...layout, pins, tabs } };
}
// ─── Compact overflow ─────────────────────────────────────────────
/** Higher = collapses into More later. filtersMenu never collapses. */
export function barItemOverflowPriority(id, descriptor) {
    if (id === 'filtersMenu')
        return Number.POSITIVE_INFINITY;
    const key = quickFieldOf(id);
    if (key)
        return descriptor.fields.find((f) => f.key === key)?.quick?.overflowPriority ?? 0;
    return descriptor.bar.overflowPriority[barPaletteId(id)] ?? 50;
}
/**
 * Split the rendered items into what fits and what collapses into the
 * trailing More menu. The user's order is preserved on both sides; the lowest
 * priority collapses first (ties: the later item first). The guaranteed
 * minimum is the Filters menu plus More, so the bar never scrolls sideways.
 */
export function planBarOverflow(items, widths, available, moreWidth, descriptor, gap = 0) {
    const width = (ids) => ids.reduce((s, id) => s + (widths[id] ?? 0), 0) + Math.max(0, ids.length - 1) * gap;
    if (width(items) <= available)
        return { visible: [...items], overflow: [] };
    const candidates = items
        .map((id, i) => ({ id, i, p: barItemOverflowPriority(id, descriptor) }))
        .filter((c) => !isRepeatableBarItem(c.id) && Number.isFinite(c.p))
        .sort((a, b) => a.p - b.p || b.i - a.i);
    const collapsed = new Set();
    let visible = [...items];
    for (const c of candidates) {
        if (width(visible) + gap + moreWidth <= available)
            break;
        collapsed.add(c.id);
        visible = tidyBarItems(items.filter((id) => !collapsed.has(id)));
    }
    return { visible, overflow: items.filter((id) => collapsed.has(id)) };
}
//# sourceMappingURL=layout.js.map